<template>
  <div class="profile__item-container">
    <div
      id="phone"
      :class="['profile__item', canEdit && 'profile__item--opened']"
      @click="toggleUpdateSection()"
    >
      <div class="profile__icon-wrapper">
        <PhoneIcon size="1x" class="profile__icon" />
      </div>
      <div class="profile__item-data-wrapper">
        <p class="profile__item-lead">Phone</p>
        <p class="profile__item-data">{{ phone || 'None' }}</p>
      </div>
      <Edit2Icon
        :class="[
          'dashboard__action-icon',
          canEdit && 'dashboard__action-icon--hide',
        ]"
      />
    </div>
    <transition name="fade">
      <div
        v-if="isUpdatingSectionOpened"
        class="profile__item-edit-wrapper edit-phone__edit-wrapper "
      >
        <div class="edit-phone__details">
          <div class="dashboard-input-wrapper edit-phone__input-wrapper">
            <select
              v-model="country"
              class="dashboard-input dashboard-select"
              required
            >
              <option
                v-for="country in countryCodes"
                :key="`country-code-${country.value}`"
                :value="country.value"
              >
                <i :class="[`em-flag-${country.value}`]"></i>
                {{ country.name }}</option
              >
            </select>
            <ChevronDownIcon class="dashboard-select-icon" />
          </div>
          <div class="dashboard-input-wrapper edit-phone__input-wrapper">
            <input
              class="dashboard-input"
              type="tel"
              placeholder="1235554567"
              v-model="phone"
              @paste="validatePhone($event)"
              @keypress="validatePhone($event)"
              :disabled="isLoading"
            />
          </div>
        </div>
        <div class="profile__item-btn-wrapper">
          <button
            v-if="!isLoading"
            @click="updatePhoneNumber()"
            class="btn btn--primary profile__item-edit-btn"
            :disabled="!canSave"
          >
            Save
          </button>
          <button
            v-if="!isLoading"
            @click="cancel()"
            class="btn btn--tertiary profile__item-edit-btn"
          >
            Cancel
          </button>
          <ActionLoader v-else name="ball-clip-rotate" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import countryCodes from '@/static/countryCodes'
import { PhoneIcon, Edit2Icon, ChevronDownIcon } from 'vue-feather-icons'
import { isPhoneNumberValid, isPhoneNumberValidWatcher } from '@/helpers'

export default {
  name: 'PhoneNumber',
  components: {
    PhoneIcon,
    ActionLoader,
    ChevronDownIcon,
    Edit2Icon,
  },
  data() {
    return {
      country: '',
      phone: '',
      isLoading: false,
      countryCodes: [
        { value: 'Select Country', name: 'Select Country' },
        ...countryCodes,
      ],
      editData: false,
      isSaveDisabled: true,
    }
  },
  props: {
    currentPhoneNumber: {
      type: String,
      required: false,
    },
    canEdit: {
      type: Boolean,
      default() {
        return false
      },
    },
  },
  watch: {
    country: {
      handler: 'updateCountryLabel',
    },
    phone: {
      handler: 'updatePhoneNumberLabel',
    },
  },
  methods: {
    async updatePhoneNumber() {
      const { phone, country } = this
      if (!phone || !phone.length) {
        this.$store.dispatch('app/setError', 'Please Enter a Phone Number')
      } else if (!country || !country.length) {
        this.$store.dispatch(
          'app/setError',
          'Please Select the Country for The Phone Number'
        )
      } else {
        const phoneNumber = parsePhoneNumberFromString(phone, country)
        if (!phoneNumber || !phoneNumber.isPossible()) {
          this.$store.dispatch('app/setError', 'Please Check the Phone Number')
        } else {
          this.isLoading = true
          try {
            await this.$store.dispatch(
              'auth/updatePhoneNumber',
              phoneNumber.number
            )
            this.$emit('is-done-editing-phone-number')
          } catch (error) {
            this.isLoading = false
          }
          this.isLoading = false
          this.isSaveDisabled = true
          this.toggleUpdateSection()
        }
      }
    },
    updateCountryLabel() {
      if (!this.phone) return
      if (this.isUpdatingSectionOpened) this.isSaveDisabled = false
      const nationalNumber = parsePhoneNumberFromString(
        this.phone.toString(),
        this.country
      )?.nationalNumber
      if (nationalNumber)
        this.phone = parsePhoneNumberFromString(
          nationalNumber,
          this.country
        )?.number
    },
    updatePhoneNumberLabel() {
      this.isSaveDisabled = !(
        this.isUpdatingSectionOpened &&
        isPhoneNumberValid(this.phone) &&
        this.phone.length > 8
      )
    },
    toggleUpdateSection() {
      this.$emit('itemActive', 'phone')
      this.editData = !this.isUpdatingSectionOpened
      if (this.isUpdatingSectionOpened) this.$emit('itemActive', '')
    },
    initialData() {
      this.phone =
        this.currentPhoneNumber !== 'None' ? this.currentPhoneNumber : null
      const phoneNumber = parsePhoneNumberFromString(this.currentPhoneNumber)
      this.country = phoneNumber?.country
        ? phoneNumber.country
        : 'Select Country'
    },
    cancel() {
      this.initialData()
      this.isSaveDisabled = true
      this.toggleUpdateSection()
    },
    validatePhone($event) {
      if ($event && $event.inputType === 'insertFromPaste')
        $event.preventDefault()
      const isPlusCharInvalid = this.phone && $event.charCode === 43
      if (isPlusCharInvalid) $event.preventDefault()
      if (
        $event.charCode === 0 ||
        (isPhoneNumberValidWatcher(String.fromCharCode($event.charCode)) &&
          (!this.phone || this.phone.length < 16))
      ) {
        return true
      }
      $event.preventDefault()
    },
  },
  computed: {
    isUpdatingSectionOpened() {
      return this.canEdit && this.editData
    },
    canSave() {
      return !this.isSaveDisabled && this.country !== 'Select Country'
    },
  },
  mounted() {
    this.initialData()
  },
}
</script>

<style lang="scss">
.edit-phone {
  &__input-wrapper {
    @include mobile {
      max-width: 250px !important;
      width: 100%;
      &:not(:first-child) {
        margin-left: 10px;
      }
    }
  }
  &__edit-wrapper {
    display: flex;
    flex-direction: column;
  }
  &__details {
    @include mobile {
      display: flex;
      width: 100%;
      align-items: center;
    }
  }
}
</style>
